import { useEffect, useState } from "react";
import { createSinglesItem, deleteSinglesItem, fetchSingles } from "../api";
import type { AppConfig, SinglesItem } from "../types";

function fmt(v: string | null | undefined) {
  if (!v) return "—";
  const n = parseFloat(v);
  return isNaN(n) ? v : n.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

function pnlVal(cost: string, market: string | null) {
  if (!market) return null;
  const c = parseFloat(cost);
  const m = parseFloat(market);
  return isNaN(c) || isNaN(m) ? null : m - c;
}

const STATUS_OPTS = ["", "in_stock", "listed", "sold"];
const GAME_OPTS = ["", "mtg", "pokemon", "yugioh", "lorcana", "onepiece", "other"];
const FINISH_OPTS = ["", "nf", "foil", "etched"];
const CONDITION_OPTS = ["", "near_mint", "lightly_played", "moderately_played", "heavily_played", "damaged"];

export default function SinglesView({ cfg }: { cfg: AppConfig }) {
  const [items, setItems] = useState<SinglesItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [status, setStatus] = useState("in_stock");
  const [game, setGame] = useState("");
  const [search, setSearch] = useState("");
  const [searchDraft, setSearchDraft] = useState("");
  const [selected, setSelected] = useState<SinglesItem | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  function load() {
    setLoading(true);
    fetchSingles(cfg, { status: status || undefined, game: game || undefined, q: search || undefined })
      .then(setItems)
      .catch((e: Error) => setErr(e.message))
      .finally(() => setLoading(false));
  }

  useEffect(load, [cfg, status, game, search]);

  function handleSearchKey(e: React.KeyboardEvent) {
    if (e.key === "Enter") setSearch(searchDraft);
  }

  async function handleDelete(item: SinglesItem) {
    if (!confirm(`Delete "${item.card_name}"?`)) return;
    try {
      await deleteSinglesItem(cfg, item.id);
      setSelected(null);
      load();
    } catch (e) { alert((e as Error).message); }
  }

  const totalCost = items.reduce((s, i) => s + parseFloat(i.cost_basis) * i.qty, 0);
  const totalMarket = items.reduce((s, i) => {
    const m = i.market_price ? parseFloat(i.market_price) : parseFloat(i.cost_basis);
    return s + m * i.qty;
  }, 0);

  return (
    <>
      <div className="view-header">
        <span className="view-title">Singles</span>
        <span className="view-count">{items.length}</span>
        <span className="view-total">
          Cost <strong>{totalCost.toLocaleString("en-US", { style: "currency", currency: "USD" })}</strong>
          {" · "}
          Market <strong style={{ color: totalMarket >= totalCost ? "var(--good)" : "var(--bad)" }}>
            {totalMarket.toLocaleString("en-US", { style: "currency", currency: "USD" })}
          </strong>
        </span>
        <button className="btn btn-primary" onClick={() => setShowCreate(true)}>+ Add</button>
      </div>

      <div className="filter-bar">
        <div className="status-tabs">
          {STATUS_OPTS.map((s) => (
            <button key={s || "all"} className={`status-tab ${status === s ? "active" : ""}`} onClick={() => setStatus(s)}>
              {s || "All"}
            </button>
          ))}
        </div>
        <select className="filter-select" value={game} onChange={(e) => setGame(e.target.value)}>
          {GAME_OPTS.map((g) => <option key={g} value={g}>{g || "All games"}</option>)}
        </select>
        <input
          className="form-input"
          style={{ width: 220, height: 30, fontSize: 12 }}
          placeholder="Search cards… (Enter)"
          value={searchDraft}
          onChange={(e) => setSearchDraft(e.target.value)}
          onKeyDown={handleSearchKey}
        />
        {search && (
          <button className="btn btn-sm" onClick={() => { setSearch(""); setSearchDraft(""); }}>✕ Clear</button>
        )}
      </div>

      <div className="table-wrap">
        {loading && <div className="state-container"><div className="spinner" /></div>}
        {err && <div className="state-container"><p style={{ color: "var(--bad)" }}>{err}</p></div>}
        {!loading && !err && (
          <table>
            <thead>
              <tr>
                <th>Card</th>
                <th>Set</th>
                <th>Finish</th>
                <th>Condition</th>
                <th>Game</th>
                <th style={{ textAlign: "right" }}>Qty</th>
                <th style={{ textAlign: "right" }}>Cost</th>
                <th style={{ textAlign: "right" }}>Market</th>
                <th style={{ textAlign: "right" }}>P&L</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const p = pnlVal(item.cost_basis, item.market_price);
                return (
                  <tr key={item.id} onClick={() => setSelected(item)} className={selected?.id === item.id ? "selected" : ""}>
                    <td style={{ fontWeight: 500 }}>{item.card_name}</td>
                    <td style={{ color: "var(--text-dim)", fontSize: 12 }}>{item.set_name ?? item.set_code ?? "—"}</td>
                    <td style={{ fontSize: 12 }}>{item.finish}</td>
                    <td style={{ fontSize: 12 }}>{item.condition}</td>
                    <td style={{ fontSize: 12, color: "var(--text-dim)" }}>{item.game}</td>
                    <td className="td-money">{item.qty}</td>
                    <td className="td-money">{fmt(item.cost_basis)}</td>
                    <td className="td-money">{fmt(item.market_price)}</td>
                    <td className="td-money" style={{ color: p == null ? "var(--text-dim)" : p >= 0 ? "var(--good)" : "var(--bad)" }}>
                      {p == null ? "—" : (p * item.qty).toLocaleString("en-US", { style: "currency", currency: "USD" })}
                    </td>
                    <td><span style={{ fontSize: 11 }}>{item.status}</span></td>
                  </tr>
                );
              })}
              {items.length === 0 && (
                <tr><td colSpan={10} style={{ textAlign: "center", color: "var(--text-dim)", padding: 32 }}>No singles found</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {selected && (
        <>
          <div className="drawer-backdrop" onClick={() => setSelected(null)} />
          <div className="drawer">
            <div className="drawer-header">
              <h2 style={{ fontSize: 13 }}>{selected.card_name}</h2>
              <button className="drawer-close" onClick={() => setSelected(null)}>✕</button>
            </div>
            <div className="drawer-body">
              <div className="drawer-section">
                <span className="drawer-section-title">Details</span>
                {[
                  ["ID", <span className="field-mono">{selected.id}</span>],
                  ["TCGPlayer ID", selected.tcgplayer_product_id ?? "—"],
                  ["Set", selected.set_name ?? selected.set_code ?? "—"],
                  ["Collector #", selected.collector_number ?? "—"],
                  ["Finish", selected.finish],
                  ["Condition", selected.condition],
                  ["Game", selected.game],
                  ["Qty", selected.qty],
                  ["Cost Basis", fmt(selected.cost_basis)],
                  ["Market Price", fmt(selected.market_price)],
                  ["Acquired", selected.acquired_at.slice(0, 10)],
                  ["Status", selected.status],
                  ["PO ID", selected.po_id ? <span className="field-mono">{selected.po_id}</span> : "—"],
                  ["Break ID", selected.break_id ? <span className="field-mono">{selected.break_id}</span> : "—"],
                  ["Notes", selected.notes ?? "—"],
                ].map(([label, value]) => (
                  <div key={String(label)} className="field-row">
                    <span className="field-label">{label}</span>
                    <span className="field-value">{value}</span>
                  </div>
                ))}
              </div>
              <button
                className="btn"
                style={{ color: "var(--bad)", borderColor: "var(--bad)" }}
                onClick={() => handleDelete(selected)}
              >
                Delete
              </button>
            </div>
          </div>
        </>
      )}

      {showCreate && (
        <CreateSingleModal
          cfg={cfg}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}
    </>
  );
}

function CreateSingleModal({ cfg, onClose, onCreated }: { cfg: AppConfig; onClose: () => void; onCreated: () => void }) {
  const [cardName, setCardName] = useState("");
  const [game, setGame] = useState("mtg");
  const [setCode, setSetCode] = useState("");
  const [setName, setSetName] = useState("");
  const [collectorNumber, setCollectorNumber] = useState("");
  const [finish, setFinish] = useState("nf");
  const [condition, setCondition] = useState("near_mint");
  const [qty, setQty] = useState("1");
  const [costBasis, setCostBasis] = useState("");
  const [acquiredAt, setAcquiredAt] = useState(new Date().toISOString().slice(0, 10));
  const [poId, setPoId] = useState("");
  const [tcgId, setTcgId] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setErr(null);
    try {
      await createSinglesItem(cfg, {
        card_name: cardName,
        game,
        set_code: setCode || null,
        set_name: setName || null,
        collector_number: collectorNumber || null,
        finish,
        condition,
        qty: parseInt(qty, 10),
        cost_basis: costBasis,
        acquired_at: acquiredAt,
        po_id: poId || null,
        tcgplayer_product_id: tcgId ? parseInt(tcgId, 10) : null,
        notes: notes || null,
      });
      onCreated();
    } catch (e) {
      setErr((e as Error).message);
      setSaving(false);
    }
  }

  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} />
      <div className="drawer">
        <div className="drawer-header">
          <h2>Add Single</h2>
          <button className="drawer-close" onClick={onClose}>✕</button>
        </div>
        <div className="drawer-body">
          <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div className="form-group">
              <label className="form-label">Card Name *</label>
              <input className="form-input" value={cardName} onChange={(e) => setCardName(e.target.value)} required />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div className="form-group">
                <label className="form-label">Game *</label>
                <select className="form-select" value={game} onChange={(e) => setGame(e.target.value)}>
                  {["mtg", "pokemon", "yugioh", "lorcana", "onepiece", "other"].map((g) => (
                    <option key={g} value={g}>{g}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Set Code</label>
                <input className="form-input" value={setCode} onChange={(e) => setSetCode(e.target.value)} placeholder="MH3" />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Set Name</label>
              <input className="form-input" value={setName} onChange={(e) => setSetName(e.target.value)} placeholder="Modern Horizons 3" />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
              <div className="form-group">
                <label className="form-label">Collector #</label>
                <input className="form-input" value={collectorNumber} onChange={(e) => setCollectorNumber(e.target.value)} placeholder="001" />
              </div>
              <div className="form-group">
                <label className="form-label">Finish *</label>
                <select className="form-select" value={finish} onChange={(e) => setFinish(e.target.value)}>
                  {FINISH_OPTS.filter(Boolean).map((f) => <option key={f} value={f}>{f}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Condition *</label>
                <select className="form-select" value={condition} onChange={(e) => setCondition(e.target.value)}>
                  {CONDITION_OPTS.filter(Boolean).map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div className="form-group">
                <label className="form-label">Qty</label>
                <input className="form-input" type="number" min="1" value={qty} onChange={(e) => setQty(e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Cost Basis *</label>
                <input className="form-input" value={costBasis} onChange={(e) => setCostBasis(e.target.value)} placeholder="0.00" required />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Acquired Date *</label>
              <input className="form-input" type="date" value={acquiredAt} onChange={(e) => setAcquiredAt(e.target.value)} required />
            </div>
            <div className="form-group">
              <label className="form-label">PO ID</label>
              <input className="form-input" value={poId} onChange={(e) => setPoId(e.target.value)} placeholder="UUID" />
            </div>
            <div className="form-group">
              <label className="form-label">TCGPlayer ID</label>
              <input className="form-input" value={tcgId} onChange={(e) => setTcgId(e.target.value)} placeholder="Numeric ID" />
            </div>
            <div className="form-group">
              <label className="form-label">Notes</label>
              <textarea className="form-textarea" value={notes} onChange={(e) => setNotes(e.target.value)} />
            </div>
            {err && <p style={{ color: "var(--bad)", fontSize: 12 }}>{err}</p>}
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn btn-primary" type="submit" disabled={saving}>{saving ? "Saving…" : "Add Single"}</button>
              <button className="btn" type="button" onClick={onClose}>Cancel</button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
